import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, Button, Card, apiErrorMessage, formatMoney, brandLocalDate, rebaseDatePrefill, useBrand } from '@aralere/shared-ui';
import type { AgingBucketKey, AgingEntityGroup, ArAgingResponse } from '@aralere/shared-types';

// AR aging (spec §7.19; Stage-4 canonical slice 8a). A point-in-time snapshot of who owes what,
// aged off each invoice's frozen dueDate and grouped by the billing ENTITY (billedToClientId), with
// the QuickBooks/Xero 5-column ladder (Current / 1-30 / 31-60 / 61-90 / 90+). All figures come from
// the stored method-aware balanceDue — this page recomputes nothing. Mobile-first: summary tiles + a
// real aging table from md up that collapses to stacked per-entity cards below. Each invoice row
// deep-links into /admin/invoices for the actual document + payment actions. Groundwork for 8b (late
// fees / reminders / suspension), which reads the same server-side aging.

const inputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';

const BUCKETS: { key: AgingBucketKey; label: string; short: string }[] = [
  { key: 'current', label: 'Current', short: 'Current' },
  { key: 'd1_30', label: '1-30 days', short: '1-30' },
  { key: 'd31_60', label: '31-60 days', short: '31-60' },
  { key: 'd61_90', label: '61-90 days', short: '61-90' },
  { key: 'd90plus', label: '90+ days', short: '90+' },
];


/** YYYY-MM-DD -> MM-DD-YYYY for display. */
function displayDate(iso: string | null): string {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${m}-${d}-${y}`;
}

/** Human overdue label for an invoice row. */
function overdueLabel(days: number): string {
  if (days > 0) return `${days} day${days === 1 ? '' : 's'} overdue`;
  if (days === 0) return 'Due today';
  return `Due in ${-days} day${-days === 1 ? '' : 's'}`;
}

/** A muted dash for a zero bucket cell so the table reads cleanly. */
function cell(n: number): string {
  return n > 0 ? formatMoney(n) : '-';
}

// The severity ladder keeps its original four distinct steps (amber-50 / amber-100 /
// orange-100 / red in light): soft warn, warn, ORANGE, red. The s104 sweep briefly
// collapsed 31-60 and 61-90 into one tone; the orange tokens restore the third step
// with a dark-capable value.
const BUCKET_TILE_TONE: Record<AgingBucketKey, string> = {
  current: 'bg-[var(--tint-slate-bg)] text-[var(--tint-slate-text)]',
  d1_30: 'bg-[var(--tint-warn-bg-soft)] text-[var(--tint-warn-text)]',
  d31_60: 'bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]',
  d61_90: 'bg-[var(--tint-orange-bg)] text-[var(--tint-orange-text)]',
  d90plus: 'bg-[var(--tint-red-bg)] text-[var(--tint-red-text)]',
};

export function AdminArAging(): JSX.Element {
  // 04 #136: the report spans brands, so the as-of is today on the PORTAL brand's clock (matching the
  // server default, admin-ar-aging.ts), and an untouched as-of follows that brand when it arrives.
  const { brand: portalBrand } = useBrand();
  const portalTz = portalBrand?.timezone ?? null;
  const [asOf, setAsOf] = useState<string>(() => brandLocalDate(portalTz));
  const [data, setData] = useState<ArAgingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async (date: string) => {
    setLoading(true);
    setErr(null);
    try {
      setData(await api.admin.arAging(date));
    } catch (e) {
      setErr(apiErrorMessage(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(asOf);
  }, [asOf, load]);

  useEffect(() => {
    setAsOf((a) => rebaseDatePrefill(a, null, portalTz));
  }, [portalTz]);

  const toggle = (id: string): void => setExpanded((m) => ({ ...m, [id]: !m[id] }));

  const groups: AgingEntityGroup[] = data?.groups ?? [];
  const hasReceivables = groups.length > 0;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-display tracking-tight">AR aging</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Outstanding client balances, aged by how far past due. Snapshot as of the date below.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-muted)]">As of</span>
            <input
              type="date"
              className={`${inputClass} md:w-[170px]`}
              value={asOf}
              max={brandLocalDate(portalTz)}
              onChange={(e) => setAsOf(e.target.value || brandLocalDate(portalTz))}
            />
          </label>
          <Button variant="secondary" onClick={() => void load(asOf)} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {err ? (
        <Card>
          <div className="p-4 text-sm text-[var(--tint-red-text)]">{err}</div>
        </Card>
      ) : null}

      {/* Aggregate summary — responsive tiles (2 up on phones, all 5 + total from md). */}
      <Card>
        <div className="p-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-6">
            {BUCKETS.map((b) => (
              <div key={b.key} className={`rounded-lg px-3 py-2 ${BUCKET_TILE_TONE[b.key]}`}>
                <div className="text-[11px] uppercase tracking-wide opacity-80">{b.label}</div>
                <div className="text-sm font-semibold tabular-nums">
                  {formatMoney(data?.totals[b.key] ?? 0)}
                </div>
              </div>
            ))}
            <div className="rounded-lg bg-[var(--color-primary)]/10 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">
                Total outstanding
              </div>
              <div className="text-sm font-semibold tabular-nums">
                {formatMoney(data?.grandTotal ?? 0)}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {loading && !data ? (
        <Card>
          <div className="p-6 text-sm text-[var(--color-muted)]">Loading aging…</div>
        </Card>
      ) : !hasReceivables ? (
        <Card>
          <div className="p-6 text-sm text-[var(--color-muted)]">
            No outstanding receivables as of {displayDate(data?.asOf ?? asOf)}. Every issued invoice is
            settled.
          </div>
        </Card>
      ) : (
        <>
          {/* Desktop / tablet: a proper aging table (horizontal scroll if it must). */}
          <Card className="hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--edge-rgb)/0.10)] text-left text-[var(--color-muted)]">
                    <th className="px-4 py-2 font-medium">Client</th>
                    {BUCKETS.map((b) => (
                      <th key={b.key} className="px-3 py-2 text-right font-medium whitespace-nowrap">
                        {b.short}
                      </th>
                    ))}
                    <th className="px-4 py-2 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <EntityRows
                      key={g.billedToClientId}
                      group={g}
                      open={!!expanded[g.billedToClientId]}
                      onToggle={() => toggle(g.billedToClientId)}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[rgb(var(--edge-rgb)/0.10)] font-semibold">
                    <td className="px-4 py-2">All clients</td>
                    {BUCKETS.map((b) => (
                      <td key={b.key} className="px-3 py-2 text-right tabular-nums">
                        {cell(data?.totals[b.key] ?? 0)}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right tabular-nums">
                      {formatMoney(data?.grandTotal ?? 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          {/* Mobile: one stacked card per entity, expandable to its invoices. */}
          <div className="space-y-3 md:hidden">
            {groups.map((g) => (
              <EntityCard
                key={g.billedToClientId}
                group={g}
                open={!!expanded[g.billedToClientId]}
                onToggle={() => toggle(g.billedToClientId)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** A deep link to the invoice in the admin Invoices view (its partition clientId + open target). */
function invoiceLink(clientId: string, invoiceId: string): string {
  return `/admin/invoices?client=${encodeURIComponent(clientId)}&open=${encodeURIComponent(invoiceId)}`;
}

function EntityTypeBadge({ group }: { group: AgingEntityGroup }): JSX.Element {
  return (
    <span
      className={
        'ml-2 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ' +
        (group.entityType === 'business' ? 'bg-[var(--tint-slate-bg)] text-[var(--tint-slate-text)]' : 'bg-[var(--tint-slate-bg)] text-[var(--tint-slate-text)]')
      }
    >
      {group.entityType === 'business' ? 'Business' : 'Consumer'}
    </span>
  );
}

/** A desktop table body block: the entity summary row + (when open) its invoice rows. */
function EntityRows({
  group,
  open,
  onToggle,
}: {
  group: AgingEntityGroup;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <>
      <tr className="border-b border-[rgb(var(--edge-rgb)/0.05)] hover:bg-[rgb(var(--edge-rgb)/0.02)]">
        <td className="px-4 py-2">
          <button type="button" onClick={onToggle} className="flex items-center gap-1 text-left">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={'transition-transform ' + (open ? 'rotate-90' : '')}
              aria-hidden="true"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span className="font-medium">{group.entityName}</span>
          </button>
          <div className="pl-5 text-xs text-[var(--color-muted)]">
            {group.invoiceCount} invoice{group.invoiceCount === 1 ? '' : 's'} · oldest due{' '}
            {displayDate(group.oldestDueDate)}
          </div>
        </td>
        {BUCKETS.map((b) => (
          <td key={b.key} className="px-3 py-2 text-right tabular-nums">
            {cell(group.buckets[b.key])}
          </td>
        ))}
        <td className="px-4 py-2 text-right font-semibold tabular-nums">{formatMoney(group.total)}</td>
      </tr>
      {open
        ? group.invoices.map((inv) => (
            <tr key={inv.invoiceId} className="bg-[rgb(var(--edge-rgb)/0.015)] text-[13px]">
              <td className="px-4 py-1.5 pl-9" colSpan={BUCKETS.length + 2}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <Link to={invoiceLink(inv.clientId, inv.invoiceId)} className="text-[var(--link-text)] hover:underline">
                    {inv.documentNumber ?? 'Issued invoice'}
                  </Link>
                  <span className="text-[var(--color-muted)]">due {displayDate(inv.dueDate)}</span>
                  <span className="text-[var(--color-muted)]">{overdueLabel(inv.daysOverdue)}</span>
                  {inv.hasPendingNotation ? (
                    <span className="rounded bg-[var(--tint-warn-bg)] px-1.5 py-0.5 text-[10px] text-[var(--tint-warn-text)]">
                      Payment reported
                    </span>
                  ) : null}
                  <span className="ml-auto font-medium tabular-nums">{formatMoney(inv.balanceDue)}</span>
                </div>
              </td>
            </tr>
          ))
        : null}
    </>
  );
}

/** A mobile stacked card: entity name/total + non-zero bucket chips + expandable invoices. */
function EntityCard({
  group,
  open,
  onToggle,
}: {
  group: AgingEntityGroup;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  const activeBuckets = BUCKETS.filter((b) => group.buckets[b.key] > 0);
  return (
    <Card>
      <div className="p-4">
        <button type="button" onClick={onToggle} className="flex w-full items-start justify-between gap-2 text-left">
          <div className="min-w-0">
            <div className="flex items-center">
              <span className="font-medium truncate">{group.entityName}</span>
              <EntityTypeBadge group={group} />
            </div>
            <div className="text-xs text-[var(--color-muted)]">
              {group.invoiceCount} invoice{group.invoiceCount === 1 ? '' : 's'} · oldest due{' '}
              {displayDate(group.oldestDueDate)}
            </div>
          </div>
          <div className="text-right">
            <div className="font-semibold tabular-nums">{formatMoney(group.total)}</div>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={'ml-auto transition-transform ' + (open ? 'rotate-180' : '')}
              aria-hidden="true"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>
        </button>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {activeBuckets.map((b) => (
            <span key={b.key} className={`rounded px-2 py-0.5 text-xs tabular-nums ${BUCKET_TILE_TONE[b.key]}`}>
              {b.short}: {formatMoney(group.buckets[b.key])}
            </span>
          ))}
        </div>

        {open ? (
          <div className="mt-3 space-y-2 border-t border-[rgb(var(--edge-rgb)/0.10)] pt-3">
            {group.invoices.map((inv) => (
              <div key={inv.invoiceId} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                <Link to={invoiceLink(inv.clientId, inv.invoiceId)} className="text-[var(--link-text)] hover:underline">
                  {inv.documentNumber ?? 'Issued invoice'}
                </Link>
                <span className="text-[var(--color-muted)]">due {displayDate(inv.dueDate)}</span>
                <span className="text-[var(--color-muted)]">{overdueLabel(inv.daysOverdue)}</span>
                {inv.hasPendingNotation ? (
                  <span className="rounded bg-[var(--tint-warn-bg)] px-1.5 py-0.5 text-[10px] text-[var(--tint-warn-text)]">
                    Payment reported
                  </span>
                ) : null}
                <span className="ml-auto font-medium tabular-nums">{formatMoney(inv.balanceDue)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
